import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Form, Badge, Pagination, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatNumber } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function RotationStock() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [periode, setPeriode] = useState('mois');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'taux_rotation', direction: 'desc' });

    useEffect(() => {
        fetchData();
    }, [periode]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/stats.php?action=rotation_stock&periode=${periode}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setData(result.data || []);
            setCurrentPage(1); // Reset à la première page après chargement
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getPeriodeLabel = () => {
        switch(periode) {
            case 'mois': return "30 derniers jours";
            case 'trimestre': return "3 derniers mois";
            case 'annee': return "12 derniers mois";
            default: return "cette période";
        }
    };

    // Tri des données
    const sortedData = React.useMemo(() => {
        const sortableData = [...data];
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                const aVal = parseFloat(a[sortConfig.key]) || 0;
                const bVal = parseFloat(b[sortConfig.key]) || 0;
                
                if (aVal < bVal) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aVal > bVal) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableData;
    }, [data, sortConfig]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedData.length / itemsPerPage);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        // Scroll en haut du tableau
        document.querySelector('.table-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Calcul des statistiques
    const totalProduits = sortedData.length;
    const rotationMoyenne = sortedData.reduce((sum, item) => sum + (parseFloat(item.taux_rotation) || 0), 0) / (totalProduits || 1);
    const stockMoyen = sortedData.reduce((sum, item) => sum + (parseInt(item.stock_actuel) || 0), 0) / (totalProduits || 1);

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Analyse de la rotation du stock...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchData}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15))',
                                            boxShadow: '0 10px 20px rgba(16, 185, 129, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-refresh-line text-success" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Rotation du stock
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Analyse sur {getPeriodeLabel()} • {sortedData.length} produits analysés
                                        </p>
                                    </div>
                                </div>
                                <Button variant="outline-secondary" onClick={() => navigate('/stats/dashboard')} className="rounded-pill px-4">
                                    <i className="ri-arrow-left-line me-2"></i>Retour
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Cartes de statistiques */}
                    <Row className="g-3 mb-4 stats-row">
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="stat-icon-wrapper bg-success-soft me-3">
                                        <i className="ri-stack-line text-success fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                            Rotation moyenne
                                        </small>
                                        <h3 className="mb-0 fw-bold text-success">{rotationMoyenne.toFixed(2)}x</h3>
                                        <small className="text-muted">sur la période</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="stat-icon-wrapper bg-info-soft me-3">
                                        <i className="ri-calculator-line text-info fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                            Stock moyen
                                        </small>
                                        <h3 className="mb-0 fw-bold text-info">{formatNumber(Math.round(stockMoyen))}</h3>
                                        <small className="text-muted">unités</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="stat-icon-wrapper bg-warning-soft me-3">
                                        <i className="ri-bar-chart-2-line text-warning fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                            Produits analysés
                                        </small>
                                        <h3 className="mb-0 fw-bold text-warning">{sortedData.length}</h3>
                                        <small className="text-muted">avec mouvements</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Filtres modernes */}
                    <Card className="filter-card border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-calendar-line me-2"></i>
                                            Période
                                        </Form.Label>
                                        <Form.Select 
                                            value={periode} 
                                            onChange={(e) => setPeriode(e.target.value)}
                                            className="filter-select py-2"
                                        >
                                            <option value="mois">📅 30 derniers jours</option>
                                            <option value="trimestre">📊 3 derniers mois</option>
                                            <option value="annee">📈 12 derniers mois</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-list-settings-line me-2"></i>
                                            Affichage
                                        </Form.Label>
                                        <Form.Select 
                                            value={itemsPerPage} 
                                            onChange={(e) => {
                                                setItemsPerPage(parseInt(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="filter-select py-2"
                                        >
                                            <option value="10">10 par page</option>
                                            <option value="20">20 par page</option>
                                            <option value="50">50 par page</option>
                                            <option value="100">100 par page</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <div className="d-flex gap-2">
                                        <Button 
                                            variant="primary" 
                                            onClick={fetchData}
                                            className="flex-grow-1 d-flex align-items-center justify-content-center gap-2 py-2"
                                            style={{
                                                background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                                border: 'none',
                                                borderRadius: '10px'
                                            }}
                                        >
                                            <i className="ri-refresh-line"></i>
                                            <span>Actualiser</span>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau avec tri */}
                    <Card className="border-0 shadow-sm table-card">
                        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="ri-refresh-line me-2 text-success"></i>
                                Détail de la rotation
                            </h5>
                            <Badge bg="success" className="px-3 py-2">
                                {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, sortedData.length)} sur {sortedData.length}
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th style={{ cursor: 'pointer',minWidth:'250px' }} onClick={() => handleSort('designation')}>
                                                Produit <i className={`ri-arrow-${sortConfig.key === 'designation' && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                            </th>
                                            <th>Catégorie</th>
                                            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('stock_actuel')}>
                                                Stock <i className={`ri-arrow-${sortConfig.key === 'stock_actuel' && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                            </th>
                                            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('sorties')}>
                                                Sorties <i className={`ri-arrow-${sortConfig.key === 'sorties' && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                            </th>
                                            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('entrees')}>
                                                Entrées <i className={`ri-arrow-${sortConfig.key === 'entrees' && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                            </th>
                                            <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('taux_rotation')}>
                                                Taux <i className={`ri-arrow-${sortConfig.key === 'taux_rotation' && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                            </th>
                                            <th className="text-center">Performance</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((item, index) => {
                                            const taux = parseFloat(item.taux_rotation) || 0;
                                            let performanceVariant = 'danger';
                                            let performanceLabel = 'Faible';
                                            
                                            if (taux >= 3) {
                                                performanceVariant = 'success';
                                                performanceLabel = 'Élevée';
                                            } else if (taux >= 1) {
                                                performanceVariant = 'warning';
                                                performanceLabel = 'Moyenne';
                                            }
                                            
                                            return (
                                                <tr key={item.id_produit} style={{
                                                    animation: `fadeIn 0.3s ease ${index * 0.03}s both`
                                                }}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                           
                                                            <div>
                                                                <Link to={`/stock/produits/${item.id_produit}`} className="text-primary fw-semibold text-decoration-none">
                                                                    {item.designation}
                                                                </Link>
                                                                <div><small className="text-muted">{item.reference}</small></div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{item.nom_categorie || <span className="text-muted">-</span>}</td>
                                                    <td className="text-end fw-bold">{item.stock_actuel}</td>
                                                    <td className="text-end text-danger fw-bold">{item.sorties}</td>
                                                    <td className="text-end text-success fw-bold">{item.entrees}</td>
                                                    <td className="text-center">
                                                        <span className="fw-bold" style={{ fontSize: '1.1rem' }}>{taux.toFixed(2)}x</span>
                                                    </td>
                                                    <td className="text-center">
                                                        <Badge 
                                                            bg={performanceVariant} 
                                                            className="rounded-pill px-3 py-2"
                                                            style={{
                                                                background: performanceVariant === 'success' ? 'linear-gradient(135deg, #10b981, #059669)' :
                                                                           performanceVariant === 'warning' ? 'linear-gradient(135deg, #f59e0b, #d97706)' :
                                                                           'linear-gradient(135deg, #dc3545, #bb2d3b)',
                                                                border: 'none'
                                                            }}
                                                        >
                                                            {performanceLabel}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-center">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Voir le produit</Tooltip>}>
                                                            <Button 
                                                                variant="outline-info" 
                                                                size="sm"
                                                                className="rounded-circle"
                                                                style={{ width: '36px', height: '36px' }}
                                                                onClick={() => navigate(`/stock/produits/${item.id_produit}`)}
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {currentItems.length === 0 && (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-1 text-muted mb-3"></i>
                                                    <h5>Aucune donnée disponible</h5>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Card.Footer className="bg-white py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="text-muted small">
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, sortedData.length)} sur {sortedData.length} produits
                                    </div>
                                    
                                    <Pagination className="mb-0">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        
                                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }
                                            
                                            return (
                                                <Pagination.Item
                                                    key={i}
                                                    active={pageNum === currentPage}
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </Pagination.Item>
                                            );
                                        })}
                                        
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            </Card.Footer>
                        )}
                    </Card>

                    {/* Légende améliorée */}
                    <Card className="border-0 shadow-sm mt-3" style={{ background: '#f8fafc' }}>
                        <Card.Body className="p-3">
                            <Row className="align-items-center">
                                <Col md={7}>
                                    <div className="d-flex align-items-center text-muted small">
                                        <i className="ri-information-line me-2 fs-5 text-info"></i>
                                        <span>
                                            <strong>Taux de rotation</strong> = (Sorties / Stock actuel) sur la période. 
                                            Un taux élevé indique une bonne rotation des stocks.
                                        </span>
                                    </div>
                                </Col>
                                <Col md={5}>
                                    <div className="d-flex justify-content-end gap-4">
                                        <div className="d-flex align-items-center">
                                            <span className="badge bg-success rounded-circle me-2" style={{ width: '12px', height: '12px' }}></span>
                                            <small>Élevée (≥3)</small>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="badge bg-warning rounded-circle me-2" style={{ width: '12px', height: '12px' }}></span>
                                            <small>Moyenne (1-3)</small>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="badge bg-danger rounded-circle me-2" style={{ width: '12px', height: '12px' }}></span>
                                            <small>Faible (≤1)</small>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important;
                    }

                    .stat-icon-wrapper {
                        width: 56px;
                        height: 56px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 16px;
                        transition: transform 0.3s ease;
                    }

                    .stat-card:hover .stat-icon-wrapper {
                        transform: scale(1.1);
                    }

                    .filter-card {
                        border-radius: 16px;
                        background: #f8fafc;
                    }

                    .filter-select {
                        border-radius: 10px;
                        border: 1px solid #e2e8f0;
                        padding: 0.5rem;
                        transition: all 0.2s ease;
                    }

                    .filter-select:focus {
                        border-color: #4361ee;
                        box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1);
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem 0.75rem;
                        white-space: nowrap;
                    }

                    .custom-table thead th:hover {
                        color: #4361ee;
                        background: #f1f5f9;
                    }

                    .custom-table tbody td {
                        padding: 1rem 0.75rem;
                        vertical-align: middle;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    .bg-success-soft {
                        background: rgba(16, 185, 129, 0.1);
                    }

                    .bg-info-soft {
                        background: rgba(13, 202, 240, 0.1);
                    }

                    .bg-warning-soft {
                        background: rgba(245, 158, 11, 0.1);
                    }

                    .tracking-wide {
                        letter-spacing: 0.5px;
                    }

                    .pagination .page-link {
                        border-radius: 8px;
                        margin: 0 2px;
                        color: #4361ee;
                        border: 1px solid #e2e8f0;
                    }

                    .pagination .page-link:hover {
                        background: #f1f5f9;
                        color: #3a56d4;
                        border-color: #cbd5e1;
                    }

                    .pagination .active .page-link {
                        background: linear-gradient(135deg, #4361ee, #3a56d4);
                        border-color: #4361ee;
                        color: white;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}